import type { ComponentProps } from "react"
import { useState } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Separator from "@/v2-ui/separator"
import Table, {
  createColumns,
  useTableQuery,
} from "@/v2-backoffice-client/components/table"
import Badge from "@/v2-backoffice-client/components/badge"
import Button from "@/v2-backoffice-client/components/button"
import { useUptimeConfigProbeListQuery } from "@/v2-backoffice-client/uptime/configProbe/list/uptime.configProbe.list.query"
import UptimeConfigProbeDeleteButton from "@/v2-backoffice-client/uptime/configProbe/delete/UptimeConfigProbeDeleteButton"
import UptimeConfigProbeDialog from "@/v2-backoffice-client/uptime/configSheet/UptimeConfigProbeDialog"
import type { UptimeConfigProbeListQuery } from "@/v2-backoffice-client/uptime/configProbe/list/__types__/uptime.configProbe.list.query"

type Probe = UptimeConfigProbeListQuery["getUptimeProbesList"]["items"][number]

const PAGE_SIZE = 200

// Server-side pagination + filtering + sorting mirrors
// CustomerTable. Phase 3 wires create/edit/delete here so the
// editorial probe set can be edited entirely from the operator UI.
function buildColumns(args: { onEdit(probe: Probe): void }) {
  const { onEdit } = args
  return createColumns<Probe>({
    enableSorting: true,
    enableHiding: true,
    columns: {
      Identifier: {
        id: "Identifier",
        label: "Identifier",
        renderCell({ row }) {
          return (
            <span className="font-mono text-xs">{row.original.Identifier}</span>
          )
        },
      },
      Name: {
        id: "Name",
        label: "Display name",
        renderCell({ row }) {
          return row.original.Name ?? "\u2014"
        },
      },
      Kind: {
        id: "Kind",
        label: "Kind",
        filter: {
          type: "select",
          options: [
            { label: "App", value: "app" },
            { label: "External", value: "external" },
          ],
        },
        renderCell({ row }) {
          return (
            <Badge
              variant={row.original.Kind === "app" ? "neutral" : "outline"}
              size="sm"
            >
              {row.original.Kind}
            </Badge>
          )
        },
      },
      Source: {
        id: "Source",
        label: "Source",
        defaultHidden: true,
        renderCell({ row }) {
          return (
            <span className="font-mono text-[11px]">{row.original.Source}</span>
          )
        },
      },
      Platform: {
        id: "Platform",
        label: "Platform",
        renderCell({ row }) {
          return row.original.Platform ?? "\u2014"
        },
      },
      Category: {
        id: "Category",
        label: "Category",
        defaultHidden: true,
        renderCell({ row }) {
          return row.original.Category ?? "\u2014"
        },
      },
      Url: {
        id: "Url",
        label: "URL",
        to: ({ row }) => row.original.Url ?? undefined,
        renderCell({ row }) {
          const url = row.original.Url
          if (!url) return "\u2014"
          return <span className="font-mono text-[11px] break-all">{url}</span>
        },
      },
      HsPath: {
        id: "HsPath",
        label: "Health path",
        defaultHidden: true,
        renderCell({ row }) {
          return row.original.HsPath ?? "\u2014"
        },
      },
      Protocol: {
        id: "Protocol",
        label: "Protocol",
        defaultHidden: true,
        renderCell({ row }) {
          return row.original.Protocol ?? "\u2014"
        },
      },
      IsEnabled: {
        id: "IsEnabled",
        label: "Enabled",
        filter: {
          type: "select",
          options: [
            { label: "Enabled", value: "true" },
            { label: "Disabled", value: "false" },
          ],
        },
        renderCell({ row }) {
          return (
            <Badge
              variant={row.original.IsEnabled ? "neutral" : "outline"}
              size="sm"
            >
              {row.original.IsEnabled ? "Enabled" : "Disabled"}
            </Badge>
          )
        },
      },
      _actions: {
        id: "_actions",
        label: " ",
        enableSorting: false,
        enableHiding: false,
        renderCell({ row }) {
          return (
            <div className="flex items-center justify-end gap-1">
              <Button
                variant="ghost"
                size="tiny"
                iconSrc="pencil"
                onClick={() => {
                  onEdit(row.original)
                }}
              >
                Edit
              </Button>
              <UptimeConfigProbeDeleteButton
                source={row.original.Source}
                identifier={row.original.Identifier}
              />
            </div>
          )
        },
      },
    },
  })
}

const COLUMN_SIZING = {
  Identifier: 220,
  Name: 220,
  Kind: 90,
  Platform: 130,
  Url: 280,
  IsEnabled: 100,
  _actions: 110,
}

export type UptimeConfigProbesTabProps = ComponentProps<"div">

function UptimeConfigProbesTab(props: UptimeConfigProbesTabProps) {
  const { className, ...divProps } = props

  const [editing, setEditing] = useState<Probe | null>(null)
  const [isCreateOpen, setIsCreateOpen] = useState(false)

  const columns = buildColumns({
    onEdit: (p) => {
      setEditing(p)
    },
  })

  const {
    items,
    pageInfo,
    loading,
    error,
    refetch,
    onFetchMore,
    onQueryChange,
  } = useTableQuery({
    pageSize: PAGE_SIZE,
    useQuery: useUptimeConfigProbeListQuery,
    select: (d) => d.getUptimeProbesList,
  })

  return (
    <div
      className={cn("uptime-config-probes-tab flex flex-col h-full", className)}
      {...divProps}
    >
      <Table<Probe>
        tableId="uptime-config-probes"
        data={items}
        columns={columns}
        initialColumnSizing={COLUMN_SIZING}
        manualSorting
        manualFiltering
        onQueryChange={onQueryChange}
        getRowId={(p) => `${p.Source}|${p.Identifier}`}
      >
        <Table.Toolbar>
          <div className="flex-1" />
          <Table.Search column="Identifier" placeholder="Search by id..." />
          <Button
            variant="outline"
            size="sm"
            iconSrc="plus"
            onClick={() => {
              setIsCreateOpen(true)
            }}
          >
            New probe
          </Button>
          <Table.ColumnButton />
          <Separator orientation="vertical" />
          <Table.ToolbarMenu
            onRefresh={refetch}
            exportFileName="uptime-probes"
          />
        </Table.Toolbar>

        <Table.FilterList />

        <Table.Content
          countLabel="probes"
          isLoading={loading}
          error={error}
          pageInfo={pageInfo}
          onFetchMore={onFetchMore}
          renderEmpty={
            <div className="text-sm text-muted-foreground text-center py-10">
              No probes seeded yet. The seed sproc populates this table on first
              deploy.
            </div>
          }
        />
      </Table>

      <UptimeConfigProbeDialog
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        mode="create"
      />
      <UptimeConfigProbeDialog
        open={editing !== null}
        onOpenChange={(o) => {
          if (!o) setEditing(null)
        }}
        mode="edit"
        probe={editing}
      />
    </div>
  )
}

export default UptimeConfigProbesTab
